import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Logo from "@/components/base/Logo";

const LANGS = ["AZ", "RU", "EN"];

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const isHome = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const changeLang = (lang: string) => {
    i18n.changeLanguage(lang.toLowerCase());
  };

  const currentLang = i18n.language?.toUpperCase().slice(0, 2) || "AZ";

  const navLinks = [
    { label: t("nav.home"), to: "/" },
    { label: t("nav.pricing"), to: "/pricing" },
    { label: t("nav.support"), to: "/support" },
  ];

  const baseClass = isHome && !scrolled
    ? "fixed top-0 left-0 right-0 z-50 transition-all duration-300"
    : "fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-sm transition-all duration-300";

  const textClass = isHome && !scrolled ? "text-white" : "text-gray-800";

  return (
    <header className={baseClass}>
      <nav className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="cursor-pointer">
          <Logo variant={isHome && !scrolled ? "light" : "dark"} size="md" />
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-6">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`text-sm font-medium whitespace-nowrap transition-colors cursor-pointer hover:opacity-70 ${textClass} ${location.pathname === link.to ? "font-semibold" : ""}`}
            >
              {link.label}
            </Link>
          ))}
        </div>

        {/* Right Section */}
        <div className="hidden md:flex items-center gap-3">
          {/* Language Switcher */}
          <div className="flex items-center gap-1 bg-black/10 rounded-full px-1 py-1">
            {LANGS.map((lang) => (
              <button
                key={lang}
                onClick={() => changeLang(lang)}
                className={`text-xs font-semibold px-2.5 py-1 rounded-full cursor-pointer transition-all whitespace-nowrap ${
                  currentLang === lang
                    ? "bg-white text-gray-900 shadow-sm"
                    : `${isHome && !scrolled ? "text-white/80 hover:text-white" : "text-gray-600 hover:text-gray-900"}`
                }`}
              >
                {lang}
              </button>
            ))}
          </div>
          <Link
            to="/login"
            className={`text-sm font-medium px-4 py-1.5 rounded-full border whitespace-nowrap cursor-pointer transition-all ${
              isHome && !scrolled
                ? "border-white/50 text-white hover:bg-white/10"
                : "border-gray-300 text-gray-700 hover:border-gray-500"
            }`}
          >
            {t("nav.login")}
          </Link>
          <Link
            to="/register"
            className="text-sm font-semibold px-4 py-1.5 rounded-full bg-red-600 text-white hover:bg-red-700 cursor-pointer transition-all whitespace-nowrap"
          >
            {t("nav.register")}
          </Link>
        </div>

        {/* Mobile Hamburger */}
        <button
          className={`md:hidden w-9 h-9 flex items-center justify-center cursor-pointer ${textClass}`}
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Menu"
        >
          <i className={`text-xl ${menuOpen ? "ri-close-line" : "ri-menu-line"}`} />
        </button>
      </nav>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-white shadow-lg border-t border-gray-100 px-4 py-4 flex flex-col gap-3">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              onClick={() => setMenuOpen(false)}
              className="text-gray-800 font-medium py-2 border-b border-gray-50 cursor-pointer"
            >
              {link.label}
            </Link>
          ))}
          <div className="flex items-center gap-2 pt-2">
            {LANGS.map((lang) => (
              <button
                key={lang}
                onClick={() => { changeLang(lang); setMenuOpen(false); }}
                className={`text-xs font-semibold px-3 py-1.5 rounded-full cursor-pointer border transition-all ${
                  currentLang === lang ? "bg-red-600 text-white border-red-600" : "border-gray-300 text-gray-600"
                }`}
              >
                {lang}
              </button>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
